import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function AcilFiberArizaMudahalesi() {
  useEffect(() => {
    document.title = 'Acil Fiber Arıza Müdahalesi | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '7/24 acil fiber optik arıza müdahale hizmeti. Kesilen, kopan veya hasar gören fiber kablolar için hızlı onarım ve OTDR testi ile kalite garantisi.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/acil-fiber-ariza-mudahalesi`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Acil Fiber Arıza Müdahalesi | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = '7/24 acil fiber optik arıza müdahale hizmeti. Kesilen, kopan veya hasar gören fiber kablolar için hızlı onarım ve OTDR testi ile kalite garantisi.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/acil-fiber-ariza-mudahalesi`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Acil Fiber Arıza Müdahalesi",
      "description": "7/24 acil fiber optik arıza müdahale hizmeti. Kesilen, kopan veya hasar gören fiber kablolar için hızlı onarım ve OTDR testi ile kalite garantisi.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/acil-fiber-ariza-mudahalesi`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'Acil Fiber Arıza Müdahalesi' }
  ];

  const faqData = [
    {
      question: "Acil fiber arıza müdahale süresi ne kadar?",
      answer: "Bursa merkez için 30 dakika, çevre ilçeler için 1 saat içinde sahada oluyoruz. 7/24 kesintisiz hizmet veriyoruz."
    },
    {
      question: "Fiber kablo tamiri ne kadar sürer?",
      answer: "Basit kesiklerde 1-2 saat, karmaşık arızalarda 4-6 saat sürer. Fusion splicing ile kalıcı çözüm sağlarız."
    },
    {
      question: "Hangi durumlarda acil müdahale gerekir?",
      answer: "İnternet kesintisi, yavaşlık, fiber kablo kesimi, bağlantı kaybı ve ağ arızalarında hemen arayın."
    },
    {
      question: "OTDR test neden yapılır?",
      answer: "Arıza tespiti ve onarım kalitesini kontrol etmek için yapılır. Fiber kablonun durumunu tam olarak belirler."
    },
    {
      question: "Acil müdahale ücreti nasıl hesaplanır?",
      answer: "Mesafe, arıza türü ve müdahale saatine göre belirlenir. Şeffaf fiyatlandırma ile hizmet veriyoruz."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/334b156806a325244f4a78ba8dfe47a0.jpg"
              alt="Bursa Acil Fiber Arıza"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Acil Fiber Arıza Müdahalesi
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Bursa İli 7/24 Acil Fiber Kablo Tamiri ve Anında Müdahale Hizmeti
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-red-500 text-white font-bold rounded-full hover:bg-red-600 transition-colors whitespace-nowrap animate-pulse"
                >
                  <i className="ri-phone-line mr-2"></i>
                  ACİL: +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Acil Müdahale Bilgileri */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Bursa 7/24 Acil Fiber Arıza
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Bursa ilinde 7/24 kesintisiz acil fiber arıza müdahale hizmeti veriyoruz. İnternet kesintiniz mi var? Fiber kablonuz mu kesildi? Hemen arayın, 30 dakika içinde sahadayız!
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>30 dakika</strong> içinde sahada müdahale</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>7/24 acil fiber arıza</strong> müdahale hizmeti</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Fiber kablo tamiri</strong> ve onarım</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Fusion splicing hizmeti</strong> ile kalıcı çözüm</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>OTDR test</strong> ile arıza tespiti</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=emergency%20fiber%20optic%20repair%20in%20Bursa%2C%20technician%20with%20fusion%20splicer%20working%20at%20night%2C%20urgent%20cable%20repair%2C%20emergency%20response%20van%2C%20professional%20fiber%20repair%20equipment&width=800&height=600&seq=bursa-emergency-repair&orientation=landscape"
                  alt="Bursa Acil Fiber Tamiri"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Acil Durum İstatistikleri */}
        <section className="py-20 bg-red-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Acil Müdahale Performansımız
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-timer-line text-4xl text-red-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-red-600 mb-2">30 Dk</h3>
                <p className="text-gray-600">Ortalama Sahaya Varış Süresi</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-green-600 mb-2">7/24</h3>
                <p className="text-gray-600">Kesintisiz Hizmet</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-tools-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-blue-600 mb-2">%98</h3>
                <p className="text-gray-600">İlk Müdahalede Çözüm</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-3xl font-bold text-orange-600 mb-2">%100</h3>
                <p className="text-gray-600">Müşteri Memnuniyeti</p>
              </div>
            </div>
          </div>
        </section>

        {/* Acil Müdahale Hizmetleri */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Acil Fiber Arıza Hizmetlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-red-50 border-l-4 border-red-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-scissors-cut-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fiber Kablo Kesimi</h3>
                <p className="text-gray-600">
                  Kazı çalışmaları veya kaza sonucu kesilen fiber kabloların acil tamiri. Bursa genelinde hızlı müdahale.
                </p>
              </div>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-pulse-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bağlantı Arızası</h3>
                <p className="text-gray-600">
                  İnternet bağlantı kesintileri ve yavaşlık problemlerinin acil çözümü. OTDR test ile arıza tespiti.
                </p>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bina İçi Arızalar</h3>
                <p className="text-gray-600">
                  Ofis ve fabrika binalarındaki fiber kablo arızalarının acil tamiri. Bina fiber kablo tamiri hizmeti.
                </p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-link text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fusion Splicing</h3>
                <p className="text-gray-600">
                  Acil fusion splicing hizmeti ile fiber kabloların kayıpsız birleştirilmesi. OM3 OM4 fiber ek işlemi.
                </p>
              </div>
              <div className="bg-purple-50 border-l-4 border-purple-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-plug-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Konnektör Tamiri</h3>
                <p className="text-gray-600">
                  SC LC konnektör arızalarının acil tamiri ve fiber optik sonlandırma işlemleri.
                </p>
              </div>
              <div className="bg-cyan-50 border-l-4 border-cyan-500 p-8 rounded-2xl">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-global-line text-2xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Metro Ethernet Arıza</h3>
                <p className="text-gray-600">
                  Metro ethernet fiber ek bağlantı arızalarının acil çözümü ve yüksek hız bağlantı restorasyonu.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Bursa Hakkında */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Bursa%20city%20panoramic%20view%20with%20industrial%20district%2C%20modern%20buildings%2C%20telecommunications%20infrastructure%2C%20busy%20urban%20center%20with%20fiber%20optic%20network%20coverage&width=800&height=600&seq=bursa-city-view&orientation=landscape"
                  alt="Bursa Şehir Görünümü"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Bursa Fiber Arıza Müdahale Ağı
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Bursa ili genelinde 7/24 acil fiber arıza müdahale hizmeti veriyoruz. Şehrin her noktasına hızlı ulaşım ağımız ile kesintisiz hizmet sağlıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Geniş Hizmet Alanı</h4>
                      <p className="text-gray-600">Bursa merkez ve tüm ilçelerde hizmet</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-car-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Hızlı Ulaşım</h4>
                      <p className="text-gray-600">Stratejik noktalarda konuşlanmış ekipler</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-tools-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Tam Donanım</h4>
                      <p className="text-gray-600">Fusion splicer ve OTDR test cihazları</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Acil Arama Kutusu */}
        <section className="py-20 bg-red-600">
          <div className="container mx-auto px-4">
            <div className="text-center text-white">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                FİBER ARIZANIZ MI VAR?
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto">
                İnternet kesildi mi? Fiber kablo arızası mı? Hemen arayın, 30 dakika içinde sahadayız!
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-lg mx-auto">
                <div className="space-y-4">
                  <a 
                    href="tel:+905537165561" 
                    className="block w-full px-8 py-4 bg-white text-red-600 font-bold rounded-full hover:bg-gray-100 transition-colors text-lg animate-pulse"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    ACİL ARAMA: +90 553 716 55 61
                  </a>
                  <a 
                    href="https://wa.me/905537165561?text=Acil%20fiber%20arıza%20müdahale%20istiyorum" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block w-full px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors text-lg"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp ile Bildir
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}